// ═══════════════════════════════════════════════════════════════════════════
//  UNIFIED API CLIENT — the ONE place for base URL + auth + honest errors.
//
//  Why this exists (2026-07-10 ERP audit):
//  - 15+ modules hardcoded 'https://api.jaiswalcapital.com' while others used
//    five different env-var names (VITE_API_URL / VITE_API_BASE_URL /
//    VITE_BACKEND_URL / ...) — dev builds talked to mixed backends at once.
//  - Three auth schemes coexisted (X-Admin-Key, Bearer, cross-site cookies
//    that never get sent from the static-hosting origin) and many fetches silently
//    converted auth failures into "empty data", so screens showed ₹0 instead
//    of an error.
//
//  Rules:
//  - NEVER convert an error into empty data. Callers get {ok:false, error}
//    and must render an error/pending state, not zeros.
//  - All auth headers ride on every request; the server ignores what it
//    doesn't need. Cookies are NOT relied upon (cross-origin — they don't
//    flow from jaiswalcapital.com to api.jaiswalcapital.com).
// ═══════════════════════════════════════════════════════════════════════════

export const API_BASE: string =
  (import.meta.env.DEV && (import.meta.env.VITE_API_BASE_URL as string)) ||
  'https://api.jaiswalcapital.com';

export interface ApiResult<T = any> {
  ok: boolean;
  status: number;          // HTTP status; 0 = network/timeout (request never completed)
  data: T | null;
  error: string | null;    // human-readable; null when ok
}

export interface ApiOpts {
  timeoutMs?: number;      // default 12s
  headers?: Record<string, string>;
}

const authHeaders = (): Record<string, string> => {
  const h: Record<string, string> = {};
  try {
    const bearer = localStorage.getItem('jc_auth_token');
    if (bearer) h['Authorization'] = `Bearer ${bearer}`;
    const adminKey = localStorage.getItem('jaiswal_admin_key');
    if (adminKey) {
      h['X-Admin-Key'] = adminKey;    // Node ledger / KYC routes
      h['X-Admin-Token'] = adminKey;  // FastAPI _verify_admin_token routes
    }
  } catch { /* localStorage unavailable — send nothing */ }
  return h;
};

async function request<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  opts: ApiOpts = {},
): Promise<ApiResult<T>> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  // Retry idempotent GETs once on a transient timeout/network fault (e.g. the API
  // briefly restarting during a deploy) so a hiccup doesn't strand the UI on
  // "timeout". Non-GET is never retried (avoid duplicate side effects). God 2026-07-23.
  const attempts = method === 'GET' ? 2 : 1;
  let lastErr = 'network error';
  for (let attempt = 0; attempt < attempts; attempt++) {
    try {
      const r = await fetch(url, {
        method,
        headers: {
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...authHeaders(),
          ...(opts.headers || {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: AbortSignal.timeout(opts.timeoutMs ?? 12000),
      });
      let data: any = null;
      try { data = await r.json(); } catch { /* non-JSON body */ }
      if (!r.ok) {
        return {
          ok: false, status: r.status, data,
          error: data?.message || data?.detail || `HTTP ${r.status}`,
        };
      }
      // Backend convention: many routes return HTTP 200 with {status:'error'}
      // in the body (e.g. /strategy/stop — live-probed 2026-07-10). Surface it.
      if (data && typeof data === 'object' && data.status === 'error') {
        return { ok: false, status: r.status, data, error: data.message || 'server reported error' };
      }
      return { ok: true, status: r.status, data, error: null };
    } catch (e: any) {
      lastErr = e?.name === 'TimeoutError' ? 'timeout' : (e?.message || 'network error');
      if (attempt < attempts - 1) { await new Promise((res) => setTimeout(res, 600)); continue; }
      return { ok: false, status: 0, data: null, error: lastErr };
    }
  }
  return { ok: false, status: 0, data: null, error: lastErr };
}

export const apiGet  = <T = any>(path: string, opts?: ApiOpts) => request<T>('GET', path, undefined, opts);
export const apiPost = <T = any>(path: string, body?: unknown, opts?: ApiOpts) => request<T>('POST', path, body, opts);
